import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import { accesoMascotasService } from '../services/accesoMascotasService'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Badge from '../components/ui/Badge'
import { QrCode, KeyRound, AlertCircle, Search, RefreshCw, HeartHandshake, ClipboardList } from 'lucide-react'

export default function AccesoMascotas() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isVeterinario, isAdmin, isSuperAdmin, isPetSitter } = usePermissions()
  const canAccess = isVeterinario || isAdmin || isSuperAdmin || isPetSitter

  const [codigo, setCodigo] = useState('')
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [applying, setApplying] = useState(false)
  const [applyMessage, setApplyMessage] = useState('')
  const [applyError, setApplyError] = useState(null)

  const validar = async () => {
    try {
      setLoading(true)
      setError(null)
      setData(null)
      setApplyError(null)
      setApplyMessage('')

      const payload = {}
      if (token.trim()) payload.token = token.trim()
      if (!payload.token && codigo.trim()) payload.codigo = codigo.trim()

      const resp = await accesoMascotasService.validar(payload)
      if (resp.success) {
        setData(resp.data)
      } else {
        setError(resp.error || 'Acceso inválido o expirado')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al validar acceso')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const tokenFromUrl = params.get('token')
    if (!tokenFromUrl || !canAccess) return
    setToken(tokenFromUrl)
    setCodigo('')
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        setData(null)
        const resp = await accesoMascotasService.validar({ token: tokenFromUrl })
        if (resp.success) setData(resp.data)
        else setError(resp.error || 'Acceso inválido o expirado')
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Error al validar acceso')
      } finally {
        setLoading(false)
      }
    })()
  }, [location.search, canAccess])

  const postularComoCuidador = async (idMascota) => {
    try {
      setApplying(true)
      setApplyError(null)
      setApplyMessage('')
      const payload = { id_mascota: idMascota }
      if (token.trim()) payload.token = token.trim()
      else if (codigo.trim()) payload.codigo = codigo.trim()
      else if (data?.token) payload.token = data.token

      const resp = await accesoMascotasService.postular(payload)
      if (resp?.success) {
        setApplyMessage('Postulacion enviada. El dueno o superadmin debe aprobarla.')
        return
      }
      setApplyError(resp?.error || 'No se pudo registrar la postulacion')
    } catch (err) {
      setApplyError(err.response?.data?.error || err.message || 'Error al postularse')
    } finally {
      setApplying(false)
    }
  }

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-red-600 dark:text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400">No tienes permisos para acceder a esta página</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Acceso por QR/Código</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Valida un código temporal del dueño para ver mascotas sin suscripción permanente.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 mt-0.5" />
          <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <QrCode className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Validar acceso</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Código humano (ej. ABC-123)"
              placeholder="ABC-123"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              leftIcon={KeyRound}
            />
            <Input
              label="Token (si viene en enlace/QR)"
              placeholder="token..."
              value={token}
              onChange={(e) => setToken(e.target.value)}
              leftIcon={Search}
            />
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setCodigo('')
                  setToken('')
                  setError(null)
                  setData(null)
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
              <Button onClick={validar} disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                Validar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <LoadingState message="Validando acceso..." />}

      {!loading && data && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resultado</h3>
              <Badge variant="info">{data.alcance}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <div>
                  <span className="font-semibold">Dueño:</span>{' '}
                  {data.dueno ? `${data.dueno.nombre || ''} ${data.dueno.apellido || ''}`.trim() : 'N/A'}
                </div>
                <div>
                  <span className="font-semibold">Expira:</span>{' '}
                  {data.fecha_expiracion ? new Date(data.fecha_expiracion).toLocaleString('es-ES') : 'N/A'}
                </div>
              </div>

              {applyError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-red-600 dark:text-red-400 font-medium text-sm">{applyError}</p>
                </div>
              )}
              {applyMessage && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-green-700 dark:text-green-300 font-medium text-sm">{applyMessage}</p>
                </div>
              )}

              {Array.isArray(data.mascotas) && data.mascotas.length > 0 ? (
                <div className="space-y-2">
                  {data.mascotas.map((m) => (
                    <div
                      key={m.id_mascota}
                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                    >
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {m.nombre} <span className="font-normal text-gray-500">({m.especie || 'N/A'})</span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {m.raza ? `Raza: ${m.raza}` : 'Raza: N/A'}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/mascotas/${m.id_mascota}`)}
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Consultar historial y stats
                        </Button>
                        {isPetSitter ? (
                          <Button
                            onClick={() => postularComoCuidador(m.id_mascota)}
                            disabled={applying}
                          >
                            <HeartHandshake className="w-4 h-4 mr-2" />
                            {applying ? 'Enviando...' : 'Postularme como cuidador'}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={QrCode}
                  title="Sin mascotas"
                  description="El acceso no tiene mascotas asociadas o no hay datos disponibles."
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

